import {
  ArrowClockwiseIcon,
  ArrowsLeftRightIcon,
  BookOpenTextIcon,
  ChartLineIcon,
  DatabaseIcon,
  GlobeSimpleIcon,
  LifebuoyIcon,
  MagnifyingGlassIcon,
  PlusSquareIcon,
  SquaresFourIcon,
  TargetIcon,
  TreeStructureIcon,
  WrenchIcon,
  type Icon,
} from "@phosphor-icons/react"

import { pluralize } from "~/utils/formatting"

interface ToolStatus {
  tool: string
  detail?: string
}

type ToolInput = Record<string, unknown>

interface HakiTool {
  /** Past tense, once the step is done. */
  done: string
  /** Present tense, while the step runs. */
  running: string
  icon: Icon
  /** One line shown next to the step title. */
  summary?: (input: ToolInput) => string | null
  /** Shown when the step is expanded. */
  details?: (input: ToolInput) => string | null
  /** The step links out instead of expanding. */
  href?: (input: ToolInput) => string | null
  /** Internal users can expand the step to the raw tool input. */
  showRawInput?: boolean
}

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : []

const quoted = (value: unknown) => (typeof value === "string" ? `“${value}”` : null)

const termsSummary = (input: ToolInput) => stringList(input.terms).join(", ") || null

const urlOf = (input: ToolInput) => (typeof input.url === "string" ? input.url : null)

function hostnameOf(input: ToolInput): string | null {
  const url = urlOf(input)
  if (!url) return null
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

function labelledKeys(value: unknown): string | null {
  if (!Array.isArray(value)) return null
  const labels = value
    .filter((m): m is { key: string; label?: string } => typeof m?.key === "string")
    .map((m) => m.label ?? m.key)
  return labels.join(", ") || null
}

function timeLabel(time: unknown): string | null {
  if (typeof time !== "object" || time === null) return null
  const { range, preset } = time as { range?: { from: string; to: string }; preset?: unknown }
  if (range) return `${range.from.slice(0, 10)} to ${range.to.slice(0, 10)}`
  return typeof preset === "string" ? preset : null
}

function rankingLabel(ranking: unknown): string | null {
  if (typeof ranking !== "object" || ranking === null) return null
  const { limit, metricKey, direction } = ranking as {
    limit?: number
    metricKey?: string
    direction?: string
  }
  if (!limit) return null
  const by = metricKey ? ` by ${metricKey}` : ""
  const arrow = direction === "asc" ? "↑" : "↓"
  return `top ${limit}${by} ${arrow}`
}

function querySpecDetails(input: ToolInput): string | null {
  const filters = Array.isArray(input.filters)
    ? input.filters
        .map((f: { field?: unknown }) => f?.field)
        .filter((f): f is string => typeof f === "string")
    : []
  const dimensions = labelledKeys(input.dimensions)
  const parts = [
    labelledKeys(input.metrics),
    rankingLabel(input.ranking),
    timeLabel(input.time),
    dimensions && `grouped by ${dimensions}`,
    filters.length > 0 && `${pluralize(filters.length, "filter")} (${filters.join(", ")})`,
  ]
  return parts.filter(Boolean).join(" · ") || null
}

const HAKI_TOOLS: Record<string, HakiTool> = {
  run_query_spec: {
    done: "Queried data",
    running: "Pulling your numbers...",
    icon: DatabaseIcon,
    details: querySpecDetails,
    showRawInput: true,
  },
  get_glossary: {
    done: "Looked up definitions",
    running: "Checking metric definitions...",
    icon: BookOpenTextIcon,
    summary: termsSummary,
  },
  get_metric_catalog: {
    done: "Looked up metrics",
    running: "Looking up metrics...",
    icon: BookOpenTextIcon,
    summary: termsSummary,
  },
  search_help_center: {
    done: "Searched help center",
    running: "Searching how-to guides...",
    icon: LifebuoyIcon,
    summary: (input) => quoted(input.query),
  },
  search_entities: {
    done: "Searched entities",
    running: "Finding matching items...",
    icon: MagnifyingGlassIcon,
    summary: (input) =>
      [input.entity_type, quoted(input.query)].filter((p) => typeof p === "string").join(" ") ||
      null,
  },
  explain_data_setup: {
    done: "Reviewed data setup",
    running: "Checking your data setup...",
    icon: TreeStructureIcon,
  },
  get_forecast_type: {
    done: "Checked forecast type",
    running: "Checking forecast type...",
    icon: ChartLineIcon,
  },
  get_product_performance: {
    done: "Checked performance against plan",
    running: "Checking performance against plan...",
    icon: TargetIcon,
  },
  explain_npi_forecast: {
    done: "Loaded NPI forecast data",
    running: "Loading forecast data...",
    icon: ChartLineIcon,
  },
  explain_noos_forecast: {
    done: "Analysed forecast signals",
    running: "Analysing forecast signals...",
    icon: ChartLineIcon,
  },
  find_similar_variants: {
    done: "Found similar variants",
    running: "Finding similar variants...",
    icon: MagnifyingGlassIcon,
  },
  list_reports: {
    done: "Checked your reports",
    running: "Checking your reports...",
    icon: SquaresFourIcon,
  },
  list_report_tiles: {
    done: "Checked report tiles",
    running: "Checking existing tiles...",
    icon: SquaresFourIcon,
  },
  propose_create_tiles: {
    done: "Prepared tile changes",
    running: "Preparing tile changes...",
    icon: PlusSquareIcon,
  },
  propose_move_tiles: {
    done: "Prepared tile moves",
    running: "Preparing tile moves...",
    icon: ArrowsLeftRightIcon,
  },
  web_search: {
    done: "Searched the web",
    running: "Searching the web...",
    icon: GlobeSimpleIcon,
    summary: (input) => quoted(input.query),
  },
  web_fetch: {
    done: "Fetched a web page",
    running: "Reading a web page...",
    icon: GlobeSimpleIcon,
    summary: hostnameOf,
    href: urlOf,
  },
  retrying: {
    done: "Retried request",
    running: "Haki is busy, retrying...",
    icon: ArrowClockwiseIcon,
  },
}

const lookupTool = (tool: string | undefined): HakiTool | undefined =>
  tool && Object.hasOwn(HAKI_TOOLS, tool) ? HAKI_TOOLS[tool] : undefined

export function getHakiTool(tool: string): HakiTool {
  return lookupTool(tool) ?? { done: tool, running: `Running ${tool}...`, icon: WrenchIcon }
}

export function getToolStatusDisplay(
  toolStatus: ToolStatus | undefined | null,
  stale: boolean,
): { label: string; detail?: string } {
  const label =
    lookupTool(toolStatus?.tool)?.running ?? (stale ? "Still working on it..." : "Thinking...")
  return { label, detail: toolStatus?.detail }
}
